import type { ChangeEvent, CSSProperties, ReactNode } from 'react';
import dayjs from '@lib/dayjs';
import styled from 'styled-components';

type NativeDatePickerProps = {
  selected?: Date | null;
  onChange?: (date: Date | null, event?: ChangeEvent<HTMLInputElement>) => void;
  minDate?: Date | false | null;
  maxDate?: Date | false | null;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  customInput?: ReactNode;
  selectsStart?: boolean;
  selectsEnd?: boolean;
  startDate?: Date | null;
  endDate?: Date | null;
  locale?: unknown;
  dateFormat?: string;
  dateFormatCalendar?: string;
  showMonthDropdown?: boolean;
  showYearDropdown?: boolean;
  dropdownMode?: string;
  yearDropdownItemNumber?: number;
  popperPlacement?: string;
  popperClassName?: string;
};

const formatDate = (date?: Date | false | null) => {
  if (!date) return '';
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const parseDate = (value: string) => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.toDate() : null;
};

const NativeDatePicker = ({
  selected,
  onChange,
  minDate,
  maxDate,
  disabled,
  className,
  style,
}: NativeDatePickerProps) => {
  const wrapperClassName = ['react-datepicker-wrapper', className].filter(Boolean).join(' ');

  return (
    <DatePickerWrapper className={wrapperClassName} style={style}>
      <div className="react-datepicker__input-container">
        <DateInput
          aria-label="날짜"
          type="date"
          value={formatDate(selected)}
          min={formatDate(minDate) || undefined}
          max={formatDate(maxDate) || undefined}
          onChange={(event) => onChange?.(parseDate(event.target.value), event)}
          disabled={disabled}
        />
      </div>
    </DatePickerWrapper>
  );
};

export default NativeDatePicker;

const DatePickerWrapper = styled.div`
  display: inline-block;
  min-width: 0;

  .react-datepicker__input-container {
    width: 100%;
  }
`;

const DateInput = styled.input`
  width: 100%;
  min-width: 9.25rem;
  height: 2.25rem;
  padding: 0 0.75rem;
  border: 1px solid #e9e9e9;
  border-radius: 4px;
  background: #f8fcff;
  color: #333;
  font-size: 0.875rem;
  line-height: 1;
  outline: 0;

  &:focus {
    border-color: #05c8da;
  }

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;
